import React, { useState } from 'react';
import {
    IonChip,
    IonLabel,
    IonIcon,
    IonInput,
    IonText
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { getAllCommonTags } from './commonTags';
import { useExistingCareMapTags } from './useExistingCareMapTags';
import { YourTagsSection } from './YourTagsSection';
import { DrawerSectionHeading } from '../components/DrawerSectionHeading';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import './TagsField.css';

interface TagsFieldProps {
    value: string[];
    onChange: (tags: string[]) => void;
    suggestions?: string[];
    placeholder?: string;
    disabled?: boolean;
    hideHeaderInViewMode?: boolean;
    entityType?: 'note' | 'appointment' | 'task';
}

export const TagsField: React.FC<TagsFieldProps> = ({
    value = [],
    onChange,
    suggestions,
    placeholder = 'Add your own labels to organize your information',
    disabled = false,
    hideHeaderInViewMode = false,
    entityType = 'note'
}) => {
    const [inputText, setInputText] = useState('');
    const [duplicateTag, setDuplicateTag] = useState<string | null>(null);
    const [showDuplicateToast, setShowDuplicateToast] = useState(false);

    // MixPanel tracking
    const tracking = useInstance(MixPanelEventEmitter);

    // Hook for existing care map tags
    const { tags: existingTags, getTagsByRecency, loading, invalidateCache } = useExistingCareMapTags();

    // Get suggestions from common tags if not provided
    const allSuggestions = suggestions || getAllCommonTags();

    // Theme-based color palette with hex values for consistent opacity handling
    const tagColorOptions = [
        '#4A8FA8', // Teal (primary theme equivalent)
        '#8E5FC7', // Purple (tertiary theme equivalent)
        '#D45A2A', // Orange (secondary theme equivalent)
        '#4A8F6B', // Green (success theme equivalent)
        '#5A72C7', // Blue
        '#C768A3', // Pink
        '#3E4A5C', // Dark blue-gray
        '#D47A47', // Orange-brown
        '#C73E3E'  // Red
    ];

    const getTagColor = (tag: string) => {
        // Use a better distribution algorithm to utilize all colors
        let hash = 0;
        for (let i = 0; i < tag.length; i++) {
            const char = tag.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        const colorIndex = Math.abs(hash) % tagColorOptions.length;
        const color = tagColorOptions[colorIndex];

        return {
            backgroundColor: `${color}1A`, // 10% opacity
            color: color,
            border: `1px solid ${color}`
        };
    };

    // Get most recently used tags (async)
    const [yourTags, setYourTags] = useState<{ name: string; count: number; lastUsedAt: string }[]>([]);

    React.useEffect(() => {
        const loadYourTags = async () => {
            try {
                const recentTags = await getTagsByRecency();
                setYourTags(recentTags.slice(0, 8)); // Show 8 most recently used
            } catch (err) {
                // Silently fail and continue with empty tags
                setYourTags([]);
            }
        };

        if (!inputText.trim()) {
            loadYourTags();
        }
    }, [getTagsByRecency, inputText]);

    // Handle your tags display based on search input
    const hasSearchInput = inputText.trim().length > 0;

    const displayYourTags = hasSearchInput
        ? existingTags // When searching, show all matching existing tags
            .filter(tag => tag.name.toLowerCase().includes(inputText.toLowerCase()))
            .filter(tag => !value.includes(tag.name)) // Don't show already selected tags
            .slice(0, 15) // Limit display
        : yourTags // When not searching, show recent tags from state
            .filter(tag => !value.includes(tag.name)); // Don't show already selected tags

    // Smart suggested tags limiting logic
    const shouldShowSuggestedTags = value.length < 6; // Don't show if 6+ tags already selected
    const maxSuggestedTags = shouldShowSuggestedTags ? 8 : 0; // Fixed count, not dynamic

    // Filter suggestions based on input, excluding existing tags
    const existingTagNames = existingTags.map(tag => tag.name.toLowerCase());
    const filteredSuggestions = shouldShowSuggestedTags ? allSuggestions
        .filter(tag => !value.includes(tag))
        .filter(tag => !existingTagNames.includes(tag.toLowerCase())) // Don't show if already exists
        .filter(tag => !inputText || tag.toLowerCase().includes(inputText.toLowerCase()))
        .slice(0, maxSuggestedTags) // Fixed limit of 8 suggested tags
        : []; // Empty array if shouldn't show suggested tags


    // Helper function to check if tag is new to care map
    const isNewTag = (tag: string): boolean => {
        return !existingTags.some(existingTag =>
            existingTag.name.toLowerCase() === tag.toLowerCase()
        );
    };

    const handleAddTag = (
        tag: string,
        source: 'suggested' | 'your_tags' | 'custom',
        _totalSuggestions?: number,
        isNewTagToCaremap?: boolean
    ) => {
        if (tag && !value.includes(tag)) {
            const existingTag = existingTags.find(t => t.name.toLowerCase() === tag.toLowerCase());
            const isSuggestedTag = allSuggestions.includes(tag);
            const isExistingTag = !!existingTag;

            // MixPanel Analytics: Only fire Tag Added for existing tags (skip for new tags to avoid duplicate with Tag Created)
            if (!isNewTagToCaremap) {
                tracking.track('Tag Added', () => ({
                    'Entity Type': entityType,
                    'Tag Name': tag,
                    'Tag Source': source,
                    'Is Suggested Tag': isSuggestedTag,
                    'Is Existing Tag': isExistingTag,
                    'Tags Count': value.length + 1,
                    'Character Count': tag.length,
                    'Tag Usage Count': existingTag?.count ?? null
                }));
            }

            onChange([...value, tag]);
            // Invalidate cache since tags will change when this entity is saved
            invalidateCache();
        }
        setInputText('');
    };

    const handleRemoveTag = (tagToRemove: string) => {
        const existingTag = existingTags.find(t => t.name === tagToRemove);

        // MixPanel Analytics: Tag Removed
        tracking.track('Tag Removed', () => ({
            'Entity Type': entityType,
            'Tag Name': tagToRemove,
            'Tags Count': value.length - 1,
            'Tag Usage Count': existingTag?.count || 1
        }));

        onChange(value.filter(tag => tag !== tagToRemove));
    };

    // Helper to check for special characters and emojis
    const hasSpecialCharacters = (str: string): boolean => {
        return /[^a-zA-Z0-9\s]/.test(str);
    };

    const hasEmoji = (str: string): boolean => {
        const emojiRegex = /[\u{1f300}-\u{1f5ff}\u{1f900}-\u{1f9ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{2600}-\u{26ff}\u{2700}-\u{27bf}\u{1f1e6}-\u{1f1ff}\u{1f191}-\u{1f251}\u{1f004}\u{1f0cf}\u{1f170}-\u{1f171}\u{1f17e}-\u{1f17f}\u{1f18e}\u{3030}\u{2b50}\u{2b55}\u{2934}-\u{2935}\u{2b05}-\u{2b07}\u{2b1b}-\u{2b1c}\u{3297}\u{3299}\u{303d}\u{00a9}\u{00ae}\u{2122}\u{23f3}\u{24c2}\u{23e9}-\u{23ef}\u{25b6}\u{23f8}-\u{23fa}]/gu;
        return emojiRegex.test(str);
    };

    const handleCustomTag = () => {
        const trimmed = inputText.trim();
        if (trimmed) {
            // Check for duplicate with case-insensitive comparison
            const isDuplicate = value.some(existingTag => existingTag.toLowerCase() === trimmed.toLowerCase());

            if (isDuplicate) {
                // Find the exact existing tag for highlighting
                const existingTag = value.find(tag => tag.toLowerCase() === trimmed.toLowerCase());

                // Handle duplicate tag attempt
                setDuplicateTag(existingTag || trimmed);
                setShowDuplicateToast(true);
                setInputText('');

                // Remove highlight and error message after animation
                setTimeout(() => {
                    setDuplicateTag(null);
                    setShowDuplicateToast(false);
                }, 2000);
            } else {
                const isNewTagToCaremap = isNewTag(trimmed);

                // MixPanel Analytics: Tag Created (only for truly new tags)
                if (isNewTagToCaremap) {
                    tracking.track('Tag Created', () => ({
                        'Entity Type': entityType,
                        'Tag Name': trimmed,
                        'Creation Method': 'enter_key',
                        'Character Count': trimmed.length,
                        'Has Special Characters': hasSpecialCharacters(trimmed),
                        'Has Emoji': hasEmoji(trimmed)
                    }));
                }

                // Pass isNewTag flag to determine which event to fire
                handleAddTag(trimmed, 'custom', filteredSuggestions.length + displayYourTags.length, isNewTagToCaremap);
            }
        }
    };


    return (
        <div className={`tags-field ${disabled ? 'view-mode' : ''}`}>
            {!(disabled && hideHeaderInViewMode) && (
                <>
                    <DrawerSectionHeading text="Tags" />

                    <IonText color="medium">
                        <p className="tags-description">{placeholder}</p>
                    </IonText>
                </>
            )}

            {/* Selected tags */}
            {value.length > 0 && (
                <div className="tags-list">
                    {value.map(tag => (
                        <IonChip
                            key={tag}
                            className={`tag-chip ${duplicateTag === tag ? 'highlight-duplicate' : ''}`}
                            style={getTagColor(tag)}
                        >
                            <IonLabel>{tag}</IonLabel>
                            {!disabled && (
                                <IonIcon
                                    icon={closeOutline}
                                    onClick={() => handleRemoveTag(tag)}
                                />
                            )}
                        </IonChip>
                    ))}
                </div>
            )}

            {/* Show tag input only when not disabled */}
            {!disabled && (
                <div className="tag-input-section">
                    <IonInput
                        data-id="tag-input"
                        value={inputText}
                        onIonInput={(e) => setInputText(e.detail.value!)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleCustomTag();
                            } else if (e.key === 'Escape') {
                                setInputText('');
                            }
                        }}
                        placeholder="Type a tag name..."
                        fill="outline"
                        onIonBlur={() => {
                            // Delay clearing input to allow chip clicks
                            setTimeout(() => {
                                setInputText('');
                            }, 200);
                        }}
                        disabled={disabled}
                    />

                    {inputText.trim() &&
                     !filteredSuggestions.some(s => s.toLowerCase() === inputText.toLowerCase()) &&
                     !displayYourTags.some(tag => tag.name.toLowerCase() === inputText.toLowerCase()) && (
                        <div className="create-tag-hint">
                            <IonText color="medium">
                                <small>Press Enter to create "{inputText.trim()}"</small>
                            </IonText>
                        </div>
                    )}

                    {/* Show duplicate error below input */}
                    {showDuplicateToast && (
                        <div className="duplicate-tag-error">
                            <IonText color="danger">
                                <small>Tag already added</small>
                            </IonText>
                        </div>
                    )}
                </div>
            )}

            {/* Show suggestions only when not disabled */}
            {!disabled && (
                <div className="tags-suggestions">
                    {/* Unified YOUR TAGS section */}
                    {(displayYourTags.length > 0 || (loading && !hasSearchInput && yourTags.length === 0)) && (
                        <YourTagsSection
                            tags={displayYourTags}
                            searchText={inputText}
                            onTagClick={handleAddTag}
                            getTagColor={getTagColor}
                            className="your-tags-section"
                            loading={loading && !hasSearchInput && yourTags.length === 0}
                            totalSuggestions={filteredSuggestions.length + displayYourTags.length}
                        />
                    )}

                    {/* Suggested tags - show when not searching OR when searching and there are matches */}
                    {filteredSuggestions.length > 0 && (
                        <div className="suggestion-group">
                            <IonText color="medium" className="suggestion-title">
                                <small>SUGGESTED TAGS</small>
                            </IonText>
                            <div className="suggestion-chips">
                                {filteredSuggestions.map((tag, _index) => (
                                    <IonChip
                                        key={tag}
                                        className="suggestion-chip"
                                        style={getTagColor(tag)}
                                        onClick={() => handleAddTag(
                                            tag,
                                            'suggested',
                                            filteredSuggestions.length + displayYourTags.length
                                        )}
                                    >
                                        <IonLabel>{tag}</IonLabel>
                                    </IonChip>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Show message when disabled and no tags */}
            {disabled && value.length === 0 && !hideHeaderInViewMode && (
                <IonText color="medium">
                    <p style={{ fontStyle: 'italic', marginTop: 8 }}>No tags</p>
                </IonText>
            )}

        </div>
    );
};
